/**
 * Workflow Request Store — reads/writes through the in-memory store.
 *
 * Storage:  src/Data/store.ts (in-memory array)
 * Durable:  PostgreSQL via API write-through
 *
 * NO localStorage reads or writes happen in this file.
 */

import type { WorkflowRequest } from "./workflowTypes";
import {
  upsertWorkflowRequest as apiUpsert,
  type CreateWorkflowRequestPayload,
} from "../api/workflowRequests";
import {
  workflowStore,
  isWorkflowHydrated,
} from "../Data/store";

const EVT = "l4e_workflows_updated";

/** Ensure store is warm (one-time localStorage migration if API hasn't loaded). */
function ensureWarm(): void {
  // No-op: localStorage warm-start removed. Data comes from API hydration only.
}

// ── Reads ────────────────────────────────────────────────────

export function getAllWorkflows(): WorkflowRequest[] {
  ensureWarm();
  return workflowStore.slice();
}

// ── Writes ───────────────────────────────────────────────────

export function saveAllWorkflows(items: WorkflowRequest[]) {
  workflowStore.length = 0;
  for (const item of items) {
    workflowStore.push(item);
  }
  try { window.dispatchEvent(new CustomEvent(EVT)); } catch { /* ignore */ }
}

export function addOrUpdateWorkflow(item: WorkflowRequest) {
  ensureWarm();
  const wg = String(item.workflow_guid);
  const i = workflowStore.findIndex((w) => String(w.workflow_guid) === wg);
  if (i >= 0) {
    workflowStore[i] = item;
  } else {
    workflowStore.unshift(item);
  }

  // Signal React.
  try { window.dispatchEvent(new CustomEvent(EVT)); } catch { /* ignore */ }

  // DEV-1017 — Backend WorkflowGuid is typed as System.Guid. Pre-fix
  // legacy code generated `wf_TIMESTAMP_HEX` strings that fail the JSON
  // deserializer. Short-circuit before the POST so we don't spam 400s.
  // localStorage may carry legacy entries from before the fix; this
  // guard prevents them from making noise on every modification.
  const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!GUID_RE.test(String(item.workflow_guid))) {
    // Stays in the in-memory store + dispatched event so the UI still
    // reflects the action; we just skip the API round-trip.
    return;
  }

  // Write-through to API (fire-and-forget).
  const apiPayload: CreateWorkflowRequestPayload = {
    workflowGuid: item.workflow_guid,
    title: item.title,
    description: item.description,
    targetType: item.target_type,
    targetGuid: item.target_guid,
    actionType: item.action_type,
    payload: item.payload,
    status: item.status,
    version: item.version,
    submittedBy: item.submitted_by,
    assignedToGuids: item.assigned_to_guids,
    history: item.history,
  };
  apiUpsert(apiPayload).catch((err) => {
    console.warn("[workflowStore] API write-through failed:", err?.message || err);
  });
}

// ── Subscription ─────────────────────────────────────────────

export function subscribeWorkflows(cb: () => void): () => void {
  const onEvt = () => cb();
  window.addEventListener(EVT, onEvt as any);
  return () => {
    window.removeEventListener(EVT, onEvt as any);
  };
}
